import { Platform } from "react-native";
import ReactNativeBlobUtil from "react-native-blob-util";
import Share from "react-native-share";
import { generatePDF } from "react-native-html-to-pdf";

import {
    resolveLabelLayout,
    LabelLayoutOption,
    ProductSettings,
} from "../components/settings/productSettings.module";
import { buildLabelData, LabelRow } from "./productLabel";
import { generateBarcodeDetails } from "./orderReceiptGenerator";

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/** Bars are sized for the label, not for a receipt. */
const barcodeSvgFor = (sku: string, layout: LabelLayoutOption) => {
    const moduleWidth = layout.labelWidthMm >= 90 ? 2 : 1;
    const details = generateBarcodeDetails(sku, 60, moduleWidth, "CODE128");
    return details?.svg ?? "";
};

const renderLabel = (
    row: LabelRow,
    settings: ProductSettings,
    layout: LabelLayoutOption,
    currencySymbol: string,
) => {
    const fields = buildLabelData(row, settings).map((item) => {
        const value = item.key === "sellingPrice" ? `${currencySymbol}${item.value}` : item.value;
        return `<div class="field ${item.key}">${escapeHtml(value)}</div>`;
    });

    const svg = barcodeSvgFor(row.sku, layout);
    const barcode = svg
        ? `<div class="barcode">${svg}</div>`
        : `<div class="barcode fallback">* ${escapeHtml(row.sku)} *</div>`;

    return `<div class="label">
        <div class="fields">${fields.join("")}</div>
        ${barcode}
        <div class="code">${escapeHtml(row.sku)}</div>
    </div>`;
};

/**
 * Renders the whole sheet. Geometry comes from the layout chosen in Product
 * Settings, so a new layout needs no change here.
 */
export const buildLabelSheetHtml = (
    rows: LabelRow[],
    settings: ProductSettings,
    currencySymbol = "",
): string => {
    const layout = resolveLabelLayout(settings.label);
    const isThermal = layout.columns === 1 && layout.pageWidthMm < 100;

    // Explicit content height wins; otherwise type scales with the label so a
    // 25 mm roll stays legible. 1 mm is roughly 2.83 pt, and ~0.75 of the line
    // box keeps descenders inside the row.
    const baseFont = layout.contentHeightMm
        ? Math.max(4, layout.contentHeightMm * 2.835 * 0.75)
        : layout.labelHeightMm >= 38
            ? 8
            : layout.labelHeightMm >= 30
                ? 7
                : 5.5;
    const barcodeHeightMm = layout.barcodeHeightMm ?? Math.max(6, layout.labelHeightMm * 0.34);
    const barcodeWidth = layout.barcodeWidthMm ? `${layout.barcodeWidthMm}mm` : "100%";
    const contentWidth = layout.contentWidthMm ? `${layout.contentWidthMm}mm` : "100%";
    const contentHeightRule = layout.contentHeightMm ? `height: ${layout.contentHeightMm}mm;` : "";
    const contentGapMm = layout.contentGapMm ?? 0;

    const labels = rows.map((row) => renderLabel(row, settings, layout, currencySymbol)).join("");

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
    @page { size: ${layout.pageWidthMm}mm ${layout.pageHeightMm}mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        font-family: Arial, Helvetica, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .sheet {
        display: flex;
        flex-wrap: wrap;
        align-content: flex-start;
        padding: ${layout.pageMarginMm}mm;
        gap: ${layout.gapMm}mm;
    }
    .label {
        width: ${layout.labelWidthMm}mm;
        height: ${layout.labelHeightMm}mm;
        padding: 1.2mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        overflow: hidden;
        page-break-inside: avoid;
        break-inside: avoid;
        ${layout.showCutGuides ? "border: 0.2mm dashed #bdbdbd;" : ""}
        ${isThermal ? "page-break-after: always; break-after: page;" : ""}
    }
    .fields {
        width: ${contentWidth};
        text-align: center;
        line-height: 1.15;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${contentGapMm}mm;
    }
    .field {
        width: 100%;
        ${contentHeightRule}
        font-size: ${baseFont - 1}pt;
        color: #000000;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    /* The first selected field is the headline of the label. */
    .fields .field:first-child {
        font-size: ${baseFont}pt;
        font-weight: bold;
    }
    .barcode {
        width: ${barcodeWidth};
        height: ${barcodeHeightMm}mm;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .barcode svg { width: 100%; height: 100%; }
    .barcode.fallback {
        font-size: ${baseFont}pt;
        font-weight: bold;
        letter-spacing: 0.4mm;
    }
    .code {
        width: 100%;
        text-align: center;
        font-size: ${baseFont - 1.5}pt;
        letter-spacing: 0.3mm;
        white-space: nowrap;
        overflow: hidden;
    }
</style>
</head>
<body><div class="sheet">${labels}</div></body>
</html>`;
};

const MM_TO_PT = 72 / 25.4;
const toPoints = (mm: number) => Math.round(mm * MM_TO_PT);

export interface LabelPdfResult {
    filePath: string;
    fileName: string;
}

/** Writes the sheet to a PDF in the app's documents directory. */
export const generateLabelSheetPdf = async (
    html: string,
    settings: ProductSettings,
    fileName?: string,
): Promise<LabelPdfResult> => {
    const layout = resolveLabelLayout(settings.label);
    const name = fileName ?? `product-labels-${Date.now()}`;

    const { filePath } = await generatePDF({
        html,
        fileName: name,
        directory: "Documents",
        width: toPoints(layout.pageWidthMm),
        height: toPoints(layout.pageHeightMm),
        padding: 0,
        bgColor: "#FFFFFF",
        shouldPrintBackgrounds: true,
    });

    if (!filePath) {
        throw new Error("The label PDF could not be created");
    }

    return { filePath, fileName: `${name}.pdf` };
};

export interface SavedLabelSheet {
    /** Where the file ended up, for the confirmation message. */
    location: "notification" | "downloads" | "files";
    path: string;
}

/**
 * Registers the PDF with Android's DownloadManager so it appears in the
 * notification shade and the Downloads list, where tapping it opens the file.
 *
 * DownloadManager only accepts paths it can read, which since Android 10 means
 * app scoped external storage - ReactNativeBlobUtil's DownloadDir already
 * points at getExternalFilesDir(DIRECTORY_DOWNLOADS), so no public folder
 * write (and no storage permission) is involved.
 */
const saveOnAndroid = async (source: string, fileName: string): Promise<SavedLabelSheet> => {
    const downloadDir = ReactNativeBlobUtil.fs.dirs.DownloadDir;
    const target = `${downloadDir}/${fileName}`;

    if (!(await ReactNativeBlobUtil.fs.isDir(downloadDir))) {
        await ReactNativeBlobUtil.fs.mkdir(downloadDir);
    }
    if (await ReactNativeBlobUtil.fs.exists(target)) {
        await ReactNativeBlobUtil.fs.unlink(target);
    }
    await ReactNativeBlobUtil.fs.cp(source, target);

    try {
        await ReactNativeBlobUtil.android.addCompleteDownload({
            title: fileName,
            description: "Product labels",
            mime: "application/pdf",
            path: target,
            showNotification: true,
        });
        return { location: "notification", path: target };
    } catch {
        // addCompletedDownload is deprecated and some OEM builds reject it.
        // The file still has to be findable, so fall back to the shared
        // Downloads collection - just without the notification.
        await ReactNativeBlobUtil.MediaCollection.copyToMediaStore(
            { name: fileName, parentFolder: "", mimeType: "application/pdf" },
            "Download",
            source,
        );
        return { location: "downloads", path: fileName };
    }
};

/**
 * Puts the generated PDF somewhere the user can find it: the Downloads
 * notification on Android, the "Save to Files" sheet on iOS.
 */
export const saveLabelSheetPdf = async ({ filePath, fileName }: LabelPdfResult): Promise<SavedLabelSheet> => {
    if (Platform.OS === "ios") {
        await Share.open({
            url: filePath.startsWith("file://") ? filePath : `file://${filePath}`,
            type: "application/pdf",
            filename: fileName,
            saveToFiles: true,
        });
        return { location: "files", path: fileName };
    }

    return saveOnAndroid(filePath.replace("file://", ""), fileName);
};

/** Opens the saved PDF in the device's default viewer. */
export const openLabelSheetPdf = async (path: string) => {
    if (Platform.OS === "android") {
        await ReactNativeBlobUtil.android.actionViewIntent(path, "application/pdf");
        return;
    }
    await Share.open({
        url: path.startsWith("file://") ? path : `file://${path}`,
        type: "application/pdf",
    });
};
